/**
 * Workflow Cleanup Utilities
 *
 * Detects and removes orphaned references in workflow data.
 * Used to clean up corrupted workflow data from incoming_connections
 * and problematic edges.
 */

import * as Sentry from '@sentry/react';
import { CustomNode } from '@/types/node-types';
import { CustomEdge, ConnectionType } from '@/types/edge-types';
import { validateOrphanedReferences } from '@/stores/node-store';
import {
  isForEachEdge,
  isMapCollectEdge,
  isMapItemEdge,
} from '@/pages/workflow/helper/for-each-region';
import {
  getConditionalBranchLabel,
  getSwitchBranchLabels,
  isConditionalEdge,
  isBranchingStruct,
} from '@/pages/workflow/helper/functional-control-flow';

// ============================================================================
// TYPES
// ============================================================================

export interface OrphanedConnectionInfo {
  nodeId: string;
  nodeName: string;
  location: 'input_port' | 'bindable_param';
  portId?: string;
  paramFieldId?: string;
  orphanNodeId: string;
  orphanPortId: string;
}

export interface ProblematicEdgeInfo {
  edgeId: string;
  source: string;
  target: string;
  sourceHandle?: string;
  targetHandle?: string;
  issues: string[];
}

export interface EdgeTypeErrorInfo {
  edgeId: string;
  nodeId: string;
  nodeName?: string;
  portId?: string;
  paramId?: string;
  message: string;
}

export interface WorkflowAnalysisResult {
  orphanedConnections: OrphanedConnectionInfo[];
  problematicEdges: ProblematicEdgeInfo[];
  edgeTypeErrors: EdgeTypeErrorInfo[];
}

export interface WorkflowCleanupResult {
  cleanedNodes: CustomNode[];
  cleanedEdges: CustomEdge[];
  removedConnectionsCount: number;
  removedEdgesCount: number;
  fixedEdgeTypesCount: number;
}

// ============================================================================
// DETECTION FUNCTIONS
// ============================================================================

/**
 * Find all orphaned references in incoming_connections across the workflow
 */
export function findOrphanedConnections(
  nodes: CustomNode[],
): OrphanedConnectionInfo[] {
  const nodeIds = new Set(nodes.map((n) => n.id));
  const orphans: OrphanedConnectionInfo[] = [];

  // Build a map of node ID to output port IDs for port validation
  const nodeOutputPorts = new Map<string, Set<string>>();
  for (const node of nodes) {
    const outputPortIds = new Set(
      node.data.struct.output_ports?.map((p) => p.id) || [],
    );
    nodeOutputPorts.set(node.id, outputPortIds);
  }

  for (const node of nodes) {
    const struct = node.data.struct;

    // Check input_ports for orphaned connections
    for (const inputPort of struct.input_ports || []) {
      const incomingConnections = inputPort.incoming_connections || [];

      for (const connection of incomingConnections) {
        // Skip empty placeholder connections
        if (!connection.node_id || !connection.port_id) continue;

        const issues: string[] = [];

        // Check if the referenced node exists
        if (!nodeIds.has(connection.node_id)) {
          issues.push(`Node "${connection.node_id}" does not exist`);
        } else {
          // Node exists, check if the port exists
          const sourceOutputPorts = nodeOutputPorts.get(connection.node_id);
          if (sourceOutputPorts && !sourceOutputPorts.has(connection.port_id)) {
            issues.push(
              `Port "${connection.port_id}" does not exist on node "${connection.node_id}"`,
            );
          }
        }

        if (issues.length > 0) {
          orphans.push({
            nodeId: node.id,
            nodeName: struct.display_name,
            location: 'input_port',
            portId: inputPort.id,
            orphanNodeId: connection.node_id,
            orphanPortId: connection.port_id,
          });
        }
      }
    }

    // Check params bindable_config for orphaned connections
    for (const param of struct.params || []) {
      const incomingConnections =
        param.bindable_config?.input_port?.incoming_connections || [];

      for (const connection of incomingConnections) {
        // Skip empty placeholder connections
        if (!connection.node_id || !connection.port_id) continue;

        const issues: string[] = [];

        // Check if the referenced node exists
        if (!nodeIds.has(connection.node_id)) {
          issues.push(`Node "${connection.node_id}" does not exist`);
        } else {
          // Node exists, check if the port exists
          const sourceOutputPorts = nodeOutputPorts.get(connection.node_id);
          if (sourceOutputPorts && !sourceOutputPorts.has(connection.port_id)) {
            issues.push(
              `Port "${connection.port_id}" does not exist on node "${connection.node_id}"`,
            );
          }
        }

        if (issues.length > 0) {
          orphans.push({
            nodeId: node.id,
            nodeName: struct.display_name,
            location: 'bindable_param',
            paramFieldId: param.field_id,
            orphanNodeId: connection.node_id,
            orphanPortId: connection.port_id,
          });
        }
      }
    }
  }

  return orphans;
}

/**
 * Find all problematic edges in the workflow
 */
export function findProblematicEdges(
  nodes: CustomNode[],
  edges: CustomEdge[],
): ProblematicEdgeInfo[] {
  const nodeIds = new Set(nodes.map((n) => n.id));
  const problematicEdges: ProblematicEdgeInfo[] = [];

  // Build maps for port validation
  const nodeOutputPorts = new Map<string, Set<string>>();
  const nodeInputPorts = new Map<string, Set<string>>();
  const nodeBindablePorts = new Map<string, Set<string>>();

  for (const node of nodes) {
    const struct = node.data.struct;

    // Output ports
    const outputPortIds = new Set(struct.output_ports?.map((p) => p.id) || []);
    nodeOutputPorts.set(node.id, outputPortIds);

    // Input ports
    const inputPortIds = new Set(struct.input_ports?.map((p) => p.id) || []);
    nodeInputPorts.set(node.id, inputPortIds);

    // Bindable param ports
    const bindablePortIds = new Set(
      struct.params
        ?.filter((p) => p.bindable_config?.input_port?.id)
        .map((p) => p.bindable_config!.input_port!.id) || [],
    );
    nodeBindablePorts.set(node.id, bindablePortIds);
  }

  for (const edge of edges) {
    const issues: string[] = [];
    const isRepeatEdge = edge.edge_type === ConnectionType.REPEAT;
    // For-each: "each item" leaves a virtual Map handle and lands on a
    // virtual item handle; the collector lands on the Map's virtual handle.
    const isMapItem = isMapItemEdge(edge);
    const isMapCollect = isMapCollectEdge(edge);

    // Check source node exists
    if (!nodeIds.has(edge.source)) {
      issues.push(`Source node "${edge.source}" does not exist`);
    } else if (isConditionalEdge(edge)) {
      const sourceNode = nodes.find((node) => node.id === edge.source);
      const branchLabel = getConditionalBranchLabel(edge);
      // --- if/else (Phase 8b): an If branches the same way a Switch does ---
      if (!sourceNode || !isBranchingStruct(sourceNode.data.struct)) {
        issues.push('Conditional edge source is not a Switch or If node');
      } else if (
        !branchLabel ||
        !getSwitchBranchLabels(sourceNode.data.struct).includes(branchLabel)
      ) {
        issues.push('Conditional edge references an unknown branch label');
      }
    } else if (isRepeatEdge || isMapItem) {
      // RepeatUntil / For-each use a virtual source handle.
    } else if (edge.sourceHandle) {
      // Check source handle (output port) exists
      const outputPorts = nodeOutputPorts.get(edge.source);
      if (outputPorts && !outputPorts.has(edge.sourceHandle)) {
        issues.push(
          `Source handle "${edge.sourceHandle}" does not exist on node "${edge.source}"`,
        );
      }
    }

    // Check target node exists
    if (!nodeIds.has(edge.target)) {
      issues.push(`Target node "${edge.target}" does not exist`);
    } else if (isConditionalEdge(edge)) {
      // Conditional targets use an instance control handle rather than a
      // catalog data input. Node existence above is the target invariant.
    } else if (isRepeatEdge || isMapItem || isMapCollect) {
      // RepeatUntil / For-each use a virtual target handle.
    } else if (edge.targetHandle) {
      // Check target handle (input port or bindable param port) exists
      const inputPorts = nodeInputPorts.get(edge.target);
      const bindablePorts = nodeBindablePorts.get(edge.target);

      const targetHandleExists =
        (inputPorts && inputPorts.has(edge.targetHandle)) ||
        (bindablePorts && bindablePorts.has(edge.targetHandle));

      if (!targetHandleExists) {
        issues.push(
          `Target handle "${edge.targetHandle}" does not exist on node "${edge.target}"`,
        );
      }
    }

    if (issues.length > 0) {
      problematicEdges.push({
        edgeId: edge.id,
        source: edge.source,
        target: edge.target,
        sourceHandle: edge.sourceHandle,
        targetHandle: edge.targetHandle,
        issues,
      });
    }
  }

  return problematicEdges;
}

// ============================================================================
// CLEANUP FUNCTIONS
// ============================================================================

/**
 * Remove orphaned connections from nodes (returns new node array)
 */
export function removeOrphanedConnections(nodes: CustomNode[]): {
  cleanedNodes: CustomNode[];
  removedCount: number;
} {
  const nodeIds = new Set(nodes.map((n) => n.id));

  // Build a map of node ID to output port IDs for port validation
  const nodeOutputPorts = new Map<string, Set<string>>();
  for (const node of nodes) {
    const outputPortIds = new Set(
      node.data.struct.output_ports?.map((p) => p.id) || [],
    );
    nodeOutputPorts.set(node.id, outputPortIds);
  }

  let removedCount = 0;

  const cleanedNodes = nodes.map((node) => {
    const struct = { ...node.data.struct };

    // Clean input_ports
    if (struct.input_ports) {
      struct.input_ports = struct.input_ports.map((inputPort) => {
        if (!inputPort.incoming_connections) return inputPort;

        const cleanedConnections = inputPort.incoming_connections.filter(
          (conn) => {
            // Keep empty placeholders
            if (!conn.node_id || !conn.port_id) return true;

            // Check if node exists
            if (!nodeIds.has(conn.node_id)) {
              removedCount++;
              return false;
            }

            // Check if port exists on the node
            const sourceOutputPorts = nodeOutputPorts.get(conn.node_id);
            if (sourceOutputPorts && !sourceOutputPorts.has(conn.port_id)) {
              removedCount++;
              return false;
            }

            return true;
          },
        );

        // Ensure at least one empty placeholder if all connections were removed
        const finalConnections =
          cleanedConnections.length > 0
            ? cleanedConnections
            : [{ node_id: '', port_id: '' }];

        return { ...inputPort, incoming_connections: finalConnections };
      });
    }

    // Clean params bindable_config
    if (struct.params) {
      struct.params = struct.params.map((param) => {
        if (!param.bindable_config?.input_port?.incoming_connections)
          return param;

        const cleanedConnections =
          param.bindable_config.input_port.incoming_connections.filter(
            (conn) => {
              // Keep empty placeholders
              if (!conn.node_id || !conn.port_id) return true;

              // Check if node exists
              if (!nodeIds.has(conn.node_id)) {
                removedCount++;
                return false;
              }

              // Check if port exists on the node
              const sourceOutputPorts = nodeOutputPorts.get(conn.node_id);
              if (sourceOutputPorts && !sourceOutputPorts.has(conn.port_id)) {
                removedCount++;
                return false;
              }

              return true;
            },
          );

        // Ensure at least one empty placeholder if all connections were removed
        const finalConnections =
          cleanedConnections.length > 0
            ? cleanedConnections
            : [{ node_id: '', port_id: '' }];

        // Update is_bound flag based on whether there are real connections
        const hasRealConnections = finalConnections.some(
          (c) => c.node_id && c.port_id,
        );

        return {
          ...param,
          bindable_config: {
            ...param.bindable_config,
            is_bound: hasRealConnections,
            input_port: {
              ...param.bindable_config.input_port,
              incoming_connections: finalConnections,
            },
          },
        };
      });
    }

    return {
      ...node,
      data: {
        ...node.data,
        struct,
      },
    };
  });

  return {
    cleanedNodes,
    removedCount,
  };
}

/**
 * Remove problematic edges (returns new edge array)
 */
export function removeProblematicEdges(
  nodes: CustomNode[],
  edges: CustomEdge[],
): {
  cleanedEdges: CustomEdge[];
  removedEdges: CustomEdge[];
} {
  const problematicEdges = findProblematicEdges(nodes, edges);
  const problematicEdgeIds = new Set(problematicEdges.map((e) => e.edgeId));

  const removedEdges = edges.filter((e) => problematicEdgeIds.has(e.id));
  const cleanedEdges = edges.filter((e) => !problematicEdgeIds.has(e.id));

  return {
    cleanedEdges,
    removedEdges,
  };
}

/**
 * Fix edge types based on whether the target handle is a bindable param or input port.
 * If targetHandle matches a param's bindable_config.input_port.id, it's PORT_TO_PARAM.
 * Otherwise, it's PORT_TO_PORT.
 */
export function fixEdgeTypes(
  nodes: CustomNode[],
  edges: CustomEdge[],
): {
  fixedEdges: CustomEdge[];
  fixedCount: number;
} {
  // Build a map of node ID -> target handle -> param info
  const nodeParamMap = new Map<
    string,
    Map<string, { fieldId: string; portId: string }>
  >();

  for (const node of nodes) {
    const handleToParam = new Map<
      string,
      { fieldId: string; portId: string }
    >();
    const params = node.data.struct.params || [];

    for (const param of params) {
      const bindablePortId = param.bindable_config?.input_port?.id;
      if (bindablePortId) {
        handleToParam.set(bindablePortId, {
          fieldId: param.field_id,
          portId: bindablePortId,
        });
      }
    }

    nodeParamMap.set(node.id, handleToParam);
  }

  let fixedCount = 0;
  const fixedEdges = edges.map((edge) => {
    // Control-flow semantics are explicit and must never be rewritten by
    // legacy data-edge repair.
    if (
      isConditionalEdge(edge) ||
      edge.edge_type === ConnectionType.REPEAT ||
      isForEachEdge(edge)
    ) {
      return edge;
    }
    if (!edge.target || !edge.targetHandle) {
      return edge;
    }

    const targetNodeParams = nodeParamMap.get(edge.target);
    const paramInfo = targetNodeParams?.get(edge.targetHandle);

    // Determine correct edge type
    const isPortToParam = !!paramInfo;
    const correctEdgeType = isPortToParam
      ? ConnectionType.PORT_TO_PARAM
      : ConnectionType.PORT_TO_PORT;

    // Determine correct target_param_id
    const correctTargetParamId =
      isPortToParam && paramInfo ? paramInfo.fieldId : null;

    // Check if edge type or target_param_id needs fixing
    const needsEdgeTypeFix = edge.edge_type !== correctEdgeType;
    const needsParamIdFix =
      (edge.target_param_id ?? null) !== correctTargetParamId;

    if (needsEdgeTypeFix || needsParamIdFix) {
      fixedCount++;
      return {
        ...edge,
        edge_type: correctEdgeType,
        target_param_id: correctTargetParamId,
      };
    }

    return edge;
  });

  return {
    fixedEdges,
    fixedCount,
  };
}

// ============================================================================
// LOGGING FUNCTIONS
// ============================================================================

/**
 * Log orphaned connections in a readable format
 */
export function logOrphanedConnections(
  orphans: OrphanedConnectionInfo[],
): void {
  if (orphans.length === 0) {
    console.log('✅ No orphaned connections found.');
    return;
  }

  console.log(`\n❌ Found ${orphans.length} orphaned connection(s):\n`);
  console.log('─'.repeat(80));

  orphans.forEach((orphan, index) => {
    console.log(`\n[${index + 1}] Orphaned Connection`);
    console.log(`    Node: "${orphan.nodeName}" (ID: ${orphan.nodeId})`);
    console.log(`    Location: ${orphan.location}`);
    if (orphan.location === 'input_port') {
      console.log(`    Input Port ID: ${orphan.portId}`);
    } else {
      console.log(`    Param Field ID: ${orphan.paramFieldId}`);
    }
    console.log(
      `    References non-existent node: "${orphan.orphanNodeId}" port: "${orphan.orphanPortId}"`,
    );
  });

  console.log('\n' + '─'.repeat(80));
}

/**
 * Log problematic edges in a readable format
 */
export function logProblematicEdges(edges: ProblematicEdgeInfo[]): void {
  if (edges.length === 0) {
    console.log('✅ No problematic edges found.');
    return;
  }

  console.log(`\n❌ Found ${edges.length} problematic edge(s):\n`);
  console.log('─'.repeat(80));

  edges.forEach((edge, index) => {
    console.log(`\n[${index + 1}] Problematic Edge (ID: ${edge.edgeId})`);
    console.log(
      `    Source: ${edge.source} (handle: ${edge.sourceHandle || 'N/A'})`,
    );
    console.log(
      `    Target: ${edge.target} (handle: ${edge.targetHandle || 'N/A'})`,
    );
    console.log(`    Issues:`);
    edge.issues.forEach((issue) => {
      console.log(`      - ${issue}`);
    });
  });

  console.log('\n' + '─'.repeat(80));
}

/**
 * Log edge type errors in a readable format
 */
export function logEdgeTypeErrors(errors: EdgeTypeErrorInfo[]): void {
  if (errors.length === 0) {
    console.log('✅ No edge type errors found.');
    return;
  }

  console.log(`\n❌ Found ${errors.length} edge type error(s):\n`);
  console.log('─'.repeat(80));

  errors.forEach((error, index) => {
    console.log(`\n[${index + 1}] Edge Type Error (Edge ID: ${error.edgeId})`);
    if (error.nodeName) {
      console.log(`    Node: "${error.nodeName}" (ID: ${error.nodeId})`);
    } else {
      console.log(`    Node ID: ${error.nodeId}`);
    }
    if (error.portId) {
      console.log(`    Port ID: ${error.portId}`);
    }
    if (error.paramId) {
      console.log(`    Param ID: ${error.paramId}`);
    }
    console.log(`    Issue: ${error.message}`);
  });

  console.log('\n' + '─'.repeat(80));
}

// ============================================================================
// MAIN FUNCTIONS
// ============================================================================

/**
 * Analyze workflow nodes and edges for issues
 */
export function analyzeWorkflow(
  nodes: CustomNode[],
  edges: CustomEdge[],
): WorkflowAnalysisResult {
  const orphanedConnections = findOrphanedConnections(nodes);
  const problematicEdges = findProblematicEdges(nodes, edges);

  // Get edge type errors from validation
  const validationResult = validateOrphanedReferences(nodes, edges);
  const edgeTypeErrors: EdgeTypeErrorInfo[] = validationResult.errors
    .filter((error) => error.type === 'edge_type')
    .map((error) => ({
      edgeId: error.edgeId || '',
      nodeId: error.nodeId || '',
      nodeName: error.nodeName,
      portId: error.portId,
      paramId: error.paramId,
      message: error.message,
    }));

  return { orphanedConnections, problematicEdges, edgeTypeErrors };
}

/**
 * Analyze workflow and log results to console
 */
export function analyzeAndLogWorkflow(
  nodes: CustomNode[],
  edges: CustomEdge[],
): WorkflowAnalysisResult {
  console.log('\n' + '='.repeat(80));
  console.log('WORKFLOW ANALYSIS REPORT');
  console.log('='.repeat(80));
  console.log(`\nTotal nodes: ${nodes.length}`);
  console.log(`Total edges: ${edges.length}`);

  console.log('\n--- WORKFLOW JSON ---');
  console.log({
    nodes,
    edges,
  });

  const result = analyzeWorkflow(nodes, edges);

  console.log('\n--- ORPHANED CONNECTIONS ---');
  logOrphanedConnections(result.orphanedConnections);

  console.log('\n--- PROBLEMATIC EDGES ---');
  logProblematicEdges(result.problematicEdges);

  console.log('\n--- EDGE TYPE ERRORS ---');
  logEdgeTypeErrors(result.edgeTypeErrors);

  console.log('\n' + '='.repeat(80));
  console.log('SUMMARY');
  console.log('='.repeat(80));
  console.log(`Orphaned connections: ${result.orphanedConnections.length}`);
  console.log(`Problematic edges: ${result.problematicEdges.length}`);
  console.log(`Edge type errors: ${result.edgeTypeErrors.length}`);

  if (
    result.orphanedConnections.length > 0 ||
    result.problematicEdges.length > 0 ||
    result.edgeTypeErrors.length > 0
  ) {
    console.log('\n⚠️  Use cleanupWorkflow() to fix these issues.');
  } else {
    console.log('\n✅ Workflow is clean!');
  }

  return result;
}

/**
 * Synchronize incoming_connections with actual edges
 * This ensures that for every valid edge, the target node has the correct
 * incoming_connection entry and is_bound state.
 */
export function synchronizeConnectionsWithEdges(
  nodes: CustomNode[],
  edges: CustomEdge[],
): {
  synchronizedNodes: CustomNode[];
  addedConnectionsCount: number;
} {
  let addedConnectionsCount = 0;

  // Build a map of target node + handle -> source connections from edges
  const edgeConnectionsMap = new Map<
    string,
    Array<{ node_id: string; port_id: string }>
  >();

  for (const edge of edges) {
    // Branch gating is execution metadata, not a data binding. It must not
    // populate legacy incoming_connections arrays.
    if (isConditionalEdge(edge)) continue;
    if (
      !edge.target ||
      !edge.targetHandle ||
      !edge.source ||
      !edge.sourceHandle
    )
      continue;

    const key = `${edge.target}:${edge.targetHandle}`;
    if (!edgeConnectionsMap.has(key)) {
      edgeConnectionsMap.set(key, []);
    }
    // incoming_connection.node_id = edge.source (the source node ID)
    // incoming_connection.port_id = edge.sourceHandle (the source port ID)
    edgeConnectionsMap.get(key)!.push({
      node_id: edge.source,
      port_id: edge.sourceHandle,
    });
  }

  const synchronizedNodes = nodes.map((node) => {
    let nodeModified = false;
    const struct = { ...node.data.struct };

    // Synchronize input_ports
    if (struct.input_ports) {
      struct.input_ports = struct.input_ports.map((inputPort) => {
        const key = `${node.id}:${inputPort.id}`;
        const edgeConnections = edgeConnectionsMap.get(key) || [];

        // Get current valid connections (non-empty)
        const currentConnections = (
          inputPort.incoming_connections || []
        ).filter((c) => c.node_id && c.port_id);

        // Check if all edge connections are present
        const currentConnectionSet = new Set(
          currentConnections.map((c) => `${c.node_id}:${c.port_id}`),
        );

        const missingConnections = edgeConnections.filter(
          (ec) => !currentConnectionSet.has(`${ec.node_id}:${ec.port_id}`),
        );

        if (missingConnections.length > 0) {
          nodeModified = true;
          addedConnectionsCount += missingConnections.length;

          const updatedConnections = [
            ...currentConnections,
            ...missingConnections,
          ];

          return {
            ...inputPort,
            incoming_connections:
              updatedConnections.length > 0
                ? updatedConnections
                : [{ node_id: '', port_id: '' }],
          };
        }

        return inputPort;
      });
    }

    // Synchronize params bindable_config
    if (struct.params) {
      struct.params = struct.params.map((param) => {
        if (!param.bindable_config?.input_port?.id) return param;

        const key = `${node.id}:${param.bindable_config.input_port.id}`;
        const edgeConnections = edgeConnectionsMap.get(key) || [];

        // Get current valid connections (non-empty)
        const currentConnections = (
          param.bindable_config.input_port.incoming_connections || []
        ).filter((c) => c.node_id && c.port_id);

        // Check if all edge connections are present
        const currentConnectionSet = new Set(
          currentConnections.map((c) => `${c.node_id}:${c.port_id}`),
        );

        const missingConnections = edgeConnections.filter(
          (ec) => !currentConnectionSet.has(`${ec.node_id}:${ec.port_id}`),
        );

        if (missingConnections.length > 0) {
          nodeModified = true;
          addedConnectionsCount += missingConnections.length;

          const updatedConnections = [
            ...currentConnections,
            ...missingConnections,
          ];

          const hasRealConnections = updatedConnections.some(
            (c) => c.node_id && c.port_id,
          );

          return {
            ...param,
            bindable_config: {
              ...param.bindable_config,
              is_bound: hasRealConnections,
              input_port: {
                ...param.bindable_config.input_port,
                incoming_connections:
                  updatedConnections.length > 0
                    ? updatedConnections
                    : [{ node_id: '', port_id: '' }],
              },
            },
          };
        }

        // Even if no missing connections, ensure is_bound is correct
        const hasRealConnections =
          edgeConnections.length > 0 || currentConnections.length > 0;
        if (param.bindable_config.is_bound !== hasRealConnections) {
          nodeModified = true;
          return {
            ...param,
            bindable_config: {
              ...param.bindable_config,
              is_bound: hasRealConnections,
            },
          };
        }

        return param;
      });
    }

    if (!nodeModified) return node;

    return {
      ...node,
      data: {
        ...node.data,
        struct,
      },
    };
  });

  return {
    synchronizedNodes,
    addedConnectionsCount,
  };
}

/**
 * Clean up workflow by removing orphaned connections, problematic edges,
 * fixing edge types, and synchronizing incoming_connections with actual edges.
 */
export function cleanupWorkflow(
  nodes: CustomNode[],
  edges: CustomEdge[],
): WorkflowCleanupResult {
  // First remove orphaned connections from nodes
  const { cleanedNodes: step1Nodes, removedCount } =
    removeOrphanedConnections(nodes);

  // Then remove problematic edges
  const { cleanedEdges: step2Edges, removedEdges } = removeProblematicEdges(
    step1Nodes,
    edges,
  );

  // Fix edge types (PORT_TO_PARAM vs PORT_TO_PORT)
  const { fixedEdges, fixedCount } = fixEdgeTypes(step1Nodes, step2Edges);

  if (fixedCount > 0) {
    console.log(`[cleanupWorkflow] Fixed ${fixedCount} edge type(s)`);
  }

  // Finally, synchronize incoming_connections with valid edges
  // This fixes cases where edges exist but incoming_connections is missing/wrong
  const { synchronizedNodes, addedConnectionsCount } =
    synchronizeConnectionsWithEdges(step1Nodes, fixedEdges);

  if (addedConnectionsCount > 0) {
    console.log(
      `[cleanupWorkflow] Added ${addedConnectionsCount} missing connection(s) from edges`,
    );
  }

  const totalIssues =
    removedCount + removedEdges.length + fixedCount + addedConnectionsCount;
  if (totalIssues > 0) {
    Sentry.captureMessage('Workflow cleanup detected corrupted data', {
      level: 'warning',
      extra: {
        context: 'workflow-cleanup',
        removedOrphanedConnections: removedCount,
        removedProblematicEdges: removedEdges.length,
        fixedEdgeTypes: fixedCount,
        addedMissingConnections: addedConnectionsCount,
      },
    });
  }

  return {
    cleanedNodes: synchronizedNodes,
    cleanedEdges: fixedEdges,
    removedConnectionsCount: removedCount,
    removedEdgesCount: removedEdges.length,
    fixedEdgeTypesCount: fixedCount,
  };
}

/**
 * Clean up workflow and log results to console
 */
export function cleanupAndLogWorkflow(
  nodes: CustomNode[],
  edges: CustomEdge[],
): WorkflowCleanupResult {
  console.log('\n' + '='.repeat(80));
  console.log('WORKFLOW CLEANUP');
  console.log('='.repeat(80));

  const result = cleanupWorkflow(nodes, edges);

  console.log(
    `\nRemoved ${result.removedConnectionsCount} orphaned connection(s)`,
  );
  console.log(`Removed ${result.removedEdgesCount} problematic edge(s)`);
  console.log(`Fixed ${result.fixedEdgeTypesCount} edge type(s)`);

  console.log('\n' + '='.repeat(80));

  return result;
}
